import { useTranslation } from '@centric-os/helpers';
import * as yup from 'yup';
import {
  ATRIUM_TYPES,
  CBORD_TYPES,
  ITC_TYPES,
  MEALPLAN_TYPES,
  TENDER_TYPES,
  TOUCHNET_TYPES,
  TRANSACT_TYPES,
} from '../../../components';

export default () => {
  // Set Localization
  const { translate } = useTranslation();

  // Set numberTransform
  const numberTransform = (value: any) => (isNaN(value) ? undefined : value);

  // Regular expression for validating IP addresses
  const ipRegex =
    /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;

  const schema = yup.object({
    id: yup
      .string()
      .nullable()
      .when('type', {
        is: (type: string) => ITC_TYPES.includes(type) || TOUCHNET_TYPES.includes(type),
        then: (schema) => schema.required(translate(`errorMessages.required`)),
        otherwise: (schema) => schema.optional(),
      }),
    name: yup
      .string()
      .nullable()
      .test({
        name: 'test-empty-string',
        test(value, ctx) {
          if (value?.startsWith(' ')) {
            return ctx.createError({ message: translate(`errorMessages.startSpace`) });
          }
          return true;
        },
      })
      .required(translate(`errorMessages.required`))
      .max(64, translate(`errorMessages.max`, { count: 64 })),
    type: yup.string().nullable().required(translate(`errorMessages.required`)),

    keystore: yup
      .object()
      .when('type', {
        // Keystore schema for ITC types
        is: (type: string) => ITC_TYPES.includes(type),
        then: yup.object({
          certificate: yup.object({
            encoded_file: yup.string().nullable(),
            name: yup.string().nullable().required(translate(`errorMessages.required`)),
            password: yup.string().nullable().required(translate(`errorMessages.required`)),
          }),
          device_id: yup
            .number()
            .transform(numberTransform)
            .nullable()
            .required(translate(`errorMessages.required`)),
          endpoint: yup
            .string()
            .url(translate(`errorMessages.invalidURL`))
            .nullable()
            .required(translate(`errorMessages.required`)),
          vendor_id: yup.string().nullable().required(translate(`errorMessages.required`)),
          webview: yup
            .string()
            .url(translate(`errorMessages.invalidURL`))
            .nullable()
            .required(translate(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for Atrium direct
        is: (type: string) => type === MEALPLAN_TYPES.ATRIUM,
        then: yup.object({
          API_URL: yup.string().nullable().required(translate(`errorMessages.required`)),
          key: yup.string().nullable().required(translate(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for Atrium SSO
        is: (type: string) => type === MEALPLAN_TYPES.ATRIUM_SSO,
        then: yup.object({
          API_URL: yup.string().nullable().required(translate(`errorMessages.required`)),
          key: yup.string().nullable().required(translate(`errorMessages.required`)),
          client: yup.string().nullable().required(translate(`errorMessages.required`)),
          webview: yup.string().nullable().required(translate(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for TouchNet types
        is: (type: string) => TOUCHNET_TYPES.includes(type),
        then: yup.object({
          operator_id: yup.string().nullable().required(translate(`errorMessages.required`)),
          operator_password: yup.string().nullable().required(translate(`errorMessages.required`)),
          api_url: yup
            .string()
            .url(translate(`errorMessages.invalidURL`))
            .nullable()
            .required(translate(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for Blackboard types
        is: (type: string) => TRANSACT_TYPES.includes(type),
        then: yup.object({
          host: yup
            .string()
            .nullable()
            .required(translate(`errorMessages.required`))
            .matches(ipRegex, translate('errorMessages.invalidIP')),
          aes_key: yup.string().nullable().required(translate(`errorMessages.required`)),
          port: yup
            .number()
            .transform(numberTransform)
            .nullable()
            .required(translate(`errorMessages.required`)),
          vendor_number: yup
            .number()
            .transform(numberTransform)
            .nullable()
            .required(translate(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for CBORD Web
        is: (type: string) => type === MEALPLAN_TYPES.CBORD,
        then: yup.object({
          auth_url: yup.string().nullable().required(translate(`errorMessages.required`)),
          uuid: yup.string().nullable().required(translate(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for CBORD Direct
        is: (type: string) => type === MEALPLAN_TYPES.CBORD_DIRECT,
        then: yup.object({
          uuid: yup.string().nullable().required(translate(`errorMessages.required`)),
        }),
      }),
    // Config for atrium
    config: yup.object().when('type', {
      is: (type: string) => ATRIUM_TYPES.includes(type),
      then: yup.object({
        terminal_id: yup.array().of(yup.string().optional().nullable()).optional().nullable(),
      }),
    }),
    // Notes for all types except ITC
    note: yup
      .object()
      .nullable()
      .when('type', {
        is: (type: string) => !ITC_TYPES.includes(type),
        then: yup.object({
          en: yup.string().nullable().optional(),
          fr: yup.string().nullable().optional(),
        }),
      }),
    terminal_id: yup
      .array()
      .nullable()
      .when('type', {
        // Terminal id for touchnet
        is: (type: string) => TOUCHNET_TYPES.includes(type),
        then: yup
          .array()
          .of(yup.string().nullable().required(translate(`errorMessages.required`)))
          .min(1, translate(`errorMessages.minValue`, { minValue: 1 }))
          .required(translate(`errorMessages.required`)),
      })
      .when('type', {
        // Terminal ids for transact
        is: (type: string) => TRANSACT_TYPES.includes(type),
        then: yup
          .array()
          .of(
            yup
              .number()
              .nullable()
              .typeError(translate(`errorMessages.invalidNumber`, { type: 'number' }))
              .required(translate(`errorMessages.required`)),
          )
          .min(1, translate(`errorMessages.minValue`, { minValue: 1 }))
          .required(translate(`errorMessages.required`)),
      })
      .when('type', {
        // Terminal ids for CBORD
        is: (type: string) => CBORD_TYPES.includes(type),
        then: yup.array().of(yup.string().nullable().optional()).nullable().optional(),
      }),
    tax_exempt: yup
      .boolean()
      .nullable()
      .when('type', {
        // Tax exempt for touchnet
        is: (type: string) => TOUCHNET_TYPES.includes(type),
        then: yup.boolean().nullable().required(translate(`errorMessages.required`)),
      }),
    tenders: yup
      .array()
      .nullable()
      .when('type', {
        is: (type: string) => ITC_TYPES.includes(type),
        then: yup
          .array()
          .nullable()
          .of(
            yup.object({
              id: yup.string().nullable().required(translate(`errorMessages.required`)),
              tax_exempt: yup.boolean().when('type', {
                is: (type: string) => TENDER_TYPES.MEAL_EXCHANGE !== type,
                then: (schema) => schema.nullable().required(translate(`errorMessages.required`)),
                otherwise: (schema) => schema.nullable(),
              }),
              type: yup.string().nullable().required(translate(`errorMessages.required`)),
              tender_number: yup.string().nullable(),
            }),
          ),
      })
      .when('type', {
        is: (type: string) =>
          ATRIUM_TYPES.includes(type) ||
          TRANSACT_TYPES.includes(type) ||
          CBORD_TYPES.includes(type),
        then: yup
          .array()
          .nullable()
          .of(
            yup.object({
              id: yup.string().nullable().required(translate(`errorMessages.required`)),
              name: yup.string().nullable().required(translate(`errorMessages.required`)),
              max_swipes_per_transaction: yup
                .number()
                .transform(numberTransform)
                .nullable()
                .when('type', {
                  is: (type: string) => TENDER_TYPES.MEAL_EQUIVALENT === type,
                  then: (schema) => schema.nullable().required(translate(`errorMessages.required`)),
                  otherwise: (schema) => schema.nullable(),
                }),
              tax_exempt: yup.boolean().when('type', {
                is: (type: string) => TENDER_TYPES.MEAL_EXCHANGE !== type,
                then: (schema) => schema.nullable().required(translate(`errorMessages.required`)),
                otherwise: (schema) => schema.nullable(),
              }),
              type: yup.string().nullable().required(translate(`errorMessages.required`)),
            }),
          )
          .min(1, translate(`errorMessages.minValue`, { minValue: 1 }))
          .required(translate(`errorMessages.required`)),
      }),
    valid_email_domains: yup.array().when('type', {
      // Transact and Atrium valid domains
      is: (type: string) => TRANSACT_TYPES.includes(type) || ATRIUM_TYPES.includes(type),
      then: yup.array().nullable().optional().of(yup.string().nullable().optional()),
      otherwise: yup.array().nullable().optional(),
    }),
  });

  const tenderSetupSchema = yup.object({
    id: yup.string().nullable().required(translate(`errorMessages.required`)),
    max_swipes_per_transaction: yup
      .number()
      .transform(numberTransform)
      .nullable()
      .required(translate(`errorMessages.required`)),
    tax_exempt: yup.boolean().nullable().required(translate(`errorMessages.required`)),
    rates: yup
      .array()
      .of(
        yup.object({
          amount: yup
            .number()
            .transform(numberTransform)
            .nullable()
            .moreThan(0, translate(`errorMessages.greaterThan`, { greaterThan: 0 }))
            .required(translate(`errorMessages.required`)),
          startTime: yup.date().nullable().required(translate(`errorMessages.required`)),
          endTime: yup.date().nullable().required(translate(`errorMessages.required`)),
        }),
      )
      .nullable()
      .min(1)
      .required(translate(`errorMessages.required`)),
  });

  // Custom domain validation function
  const validDomain = (domain: string): boolean => {
    // Regular expression to validate domains (e.g., "example.com", "test.io")
    const domainRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(domain);
  };

  const domainsSchema = yup.object({
    domain: yup
      .string()
      .required(translate(`errorMessages.required`)) // Ensure it's required
      .test('is-valid-domain', translate(`errorMessages.invalidDomain`), (value) => {
        return validDomain(value || ''); // Validate domain if value exists
      }),
  });

  return { schema, tenderSetupSchema, domainsSchema };
};
